import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Endpoint, HttpError } from '../models';
import type { IApiResponse } from '../models/http';
import { HTTP_METHOD } from './api';
import { ClientUserService } from './client-user.service';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  constructor(
    private readonly client: HttpClient,
    private readonly ownerService: ClientUserService,
  ) {}

  request$<T>(
    method: HTTP_METHOD,
    endpoint: Endpoint,
    { body, params }: _HttpRequestProps = {},
  ): Observable<IApiResponse<T>> {
    return from(this.ownerService.getUserToken()).pipe(
      switchMap((token) => {
        const url = environment.apiBaseUrl + endpoint;
        const requestHeaders = this._getHeaders(token);
        const requestParams = this._getHttpParams(params);
        const requestBody =
          body && method !== HTTP_METHOD.GET ? body : undefined;

        return this.client
          .request<IApiResponse<T>>(method, url, {
            body: requestBody,
            headers: requestHeaders,
            params: requestParams,
            observe: 'response',
            responseType: 'json',
          })
          .pipe(
            map((res) => {
              const body = res.body as IApiResponse<T>;

              if (body.success) {
                return body as IApiResponse<T>;
              }

              throw new Error(this._handleSuccessFalseMessage(res));
            }),
            catchError((err: unknown) => this._handleError(err)),
          );
      }),
    );
  }

  /**
   * Handle the available message options for success = false
   * @param ret http server response
   * @returns string message for error rejection
   */
  private _handleSuccessFalseMessage(ret: HttpResponse<any>): string {
    let retBody: IApiResponse | undefined = ret.body ?? ret.body?.data ?? ret;
    if (retBody?.message) {
      return `${retBody?.message} (Status #${ret.status})`;
    } else if (ret && (ret as any).error?.message) {
      return `${(ret as any).error.message} (Status #${ret.status})`;
    }
    return `Unknown Error (Status #${ret.status})`;
  }

  /**
   * Cleans up params object and returns HttpParams
   * @param params
   * @returns
   */
  private _getHttpParams(params?: Record<string, any>): HttpParams {
    const result = new HttpParams({
      fromObject: Object.entries(params || {}).reduce(
        (acc, [key, val]) => {
          if (val !== null && val !== undefined) acc[key] = `${val}`;
          return acc;
        },
        {} as Record<string, string>,
      ),
    });
    return result;
  }

  /**
   * Generate the HttpHeaders for a standard api request
   * @param token
   * @returns
   */
  private _getHeaders(token: string | null): HttpHeaders {
    let headers = new HttpHeaders();
    headers = headers.append('Content-Type', 'application/json');
    headers = headers.append(
      'Ocp-Apim-Subscription-Key',
      environment.apiSubscriptionKey,
    );
    headers = headers.append('Ocp-Apim-Trace', 'false');
    headers = headers.append('X-Client-Portal', 'true');
    if (token) {
      headers = headers.append('token', token);
    }
    return headers;
  }

  /**
   * Generally handles HttpErrorResponse from http client
   * @param {HttpErrorResponse | unknown} err
   * @returns
   */
  private _handleError(err: unknown): Observable<any> {
    // Monitor and look to remove this. May be old firebase related.
    if (
      err &&
      typeof err === 'object' &&
      (err as any).statusText === 'network-request-failed'
    ) {
      console.warn('network-request-failed block triggered', err);
      return of({
        success: false,
        message: (err as any).error?.message ?? 'network-request-failed',
      } as IApiResponse);
    }

    let finalError: HttpError;

    if (err instanceof HttpErrorResponse) {
      if (err.status === 0) {
        finalError = HttpError.fromErrorOrString(
          'Network error: Unable to connect to the server. Please check your internet connection or try again later.',
          err.status,
          err.statusText,
          err.url,
        );
      } else {
        finalError = HttpError.fromHttpErrorResponse(err);
      }
    } else if (err instanceof Error) {
      finalError = HttpError.fromErrorOrString(err);
    } else if (typeof err === 'string') {
      finalError = HttpError.fromErrorOrString(err);
    } else {
      try {
        finalError = HttpError.fromErrorOrString(JSON.stringify(err));
      } catch {
        finalError = HttpError.fromErrorOrString('Unknown error object');
      }
    }

    return throwError(() => finalError);
  }
}

type _HttpRequestProps = {
  params?: Record<string, any>;
  body?: Record<string, any>;
};
